<template>
  <div class="q-gutter-y-12">
    <DateTimeInput
      :model-value="transfer.trip_to.date"
      :date-label="dateLabels.main"
      :min-date="minDate || 'now'"
      :max-date="transfer.trip_return.date || transfer.trip_to.date_end"
      :rules="[v => requiredField(v)]"
      :lazy-rules="lazyRules"
      :time-label="isHourly ? $t('label.time_date') : $t('transfer.time')"
      :combined-datetime-field="combinedDatetimeField"
      use-now
      @update:modelValue="$emit('update-trip', 'trip_to', 'date', $event)"
      @update:modelValue.once="sendTransferAnalytics('passenger_enter_datetime')"
      @focus-date.once="sendTransferAnalytics('passenger_open_date')"
      @focus-time.once="sendTransferAnalytics('passenger_open_time')"
      @set-now.once="sendTransferAnalytics('passenger_click_now')"
    />
    <q-slide-transition>
      <div v-if="isHourly">
        <DateTimeInput
          :model-value="transfer.trip_to.date_end"
          :date-label="dateLabels.end"
          :min-date="tripToDate || 'now'"
          :default-year-month="tripToDate"
          :rules="[v => requiredField(v)]"
          :lazy-rules="lazyRules"
          :time-label="$t('label.time_date')"
          :combined-datetime-field="combinedDatetimeField"
          @update:modelValue="$emit('update-trip', 'trip_to', 'date_end', $event)"
          @focus-date.once="sendTransferAnalytics('passenger_open_date_end')"
          @focus-time.once="sendTransferAnalytics('passenger_open_time_end')"
        />
        <div class="hourly-buttons flex no-wrap q-gutter-4 overflow-auto hide-scrollbar">
          <q-btn
            v-for="time in [0.5, 1, 2, 3]"
            :key="time"
            :label="$t(`times.${time === 0.5 ? '0_5' : time}`)"
            :color="activeHourlyTime === time ? 'primary' : 'grey-4'"
            :text-color="activeHourlyTime === time ? '' : 'black'"
            class="text-weight-regular fs-12"
            no-wrap
            unelevated
            rounded
            @click="setTime(time)"
          />
        </div>
      </div>
    </q-slide-transition>
    <slot name="after-to-date" />
    <q-slide-transition>
      <div v-if="isRoute">
        <q-toggle
          :model-value="isRoundTrip"
          :label="$t('label.add_return_way')"
          class="toggle-margin"
          @update:modelValue="$emit('update:isRoundTrip', $event)"
        />
      </div>
    </q-slide-transition>
    <q-slide-transition>
      <div v-if="isRoundTrip">
        <DateTimeInput
          :model-value="transfer.trip_return.date"
          :date-label="dateLabels.return"
          :min-date="tripToDate || 'now'"
          :default-year-month="tripToDate"
          :rules="[v => requiredField(v)]"
          :lazy-rules="lazyRules"
          :time-label="$t('transfer.time')"
          :combined-datetime-field="combinedDatetimeField"
          @update:modelValue="$emit('update-trip', 'trip_return', 'date', $event)"
          @update:modelValue.once="sendTransferAnalytics('passenger_enter_datetime_return')"
          @focus-date.once="sendTransferAnalytics('passenger_open_date_return')"
          @focus-time.once="sendTransferAnalytics('passenger_open_time_return')"
        />
        <slot name="after-return-date" />
      </div>
    </q-slide-transition>
  </div>
</template>

<script setup>
import { computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';

import DateTimeInput from '~components/DateTimeInput';

import useRequiredFieldRules from '~composables/useRequiredFieldRules';
import { useConfigsStore } from '~stores/configs';

const props = defineProps({
  lazyRules: {
    type: [String, Boolean],
    default: 'ondemand',
  },
  minDate: {
    type: String,
    default: undefined,
  },
  isHourly: {
    type: Boolean,
    default: false,
  },
  isRoute: {
    type: Boolean,
    default: false,
  },
  isRoundTrip: {
    type: Boolean,
    default: false,
  },
  transfer: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const { requiredField } = useRequiredFieldRules();
const configsStore = useConfigsStore();
const { configs } = storeToRefs(configsStore);

const emit = defineEmits(['update-trip', 'update:isRoundTrip', 'send-transfer-analytics']);

const moment = inject('moment');

const combinedDatetimeField = computed(() => configs.value.combined_datetime_field);

const dateLabels = computed(() => {
  if (combinedDatetimeField.value) {
    return {
      main: t('label.pickup_date'),
      end: t('label.ride_ends'),
      return: t('label.return_ride'),
    };
  }

  return {
    main: t(`label.${props.isHourly ? 'start_' : 'transfer_'}date`),
    end: t('label.end_date'),
    return: t('label.return_date'),
  };
});

const tripToDate = computed(() => {
  const value = moment(props.transfer.trip_to.date, undefined, 'en');
  return value.isValid() ? value.format('YYYY-MM-DD') : '';
});

const activeHourlyTime = computed(() => moment(props.transfer.trip_to.date_end).diff(props.transfer.trip_to.date, 'h', true));

function onInputTripTo(key, date) {
  emit('update-trip', 'trip_to', key, date.format('YYYY-MM-DDTHH:mm:00Z'));
}

function setTime(time) {
  if (!props.transfer.trip_to.date && props.transfer.trip_to.date_end && moment().diff(moment(props.transfer.trip_to.date_end).subtract(time, 'h')) < 0) {
    onInputTripTo('date', moment(props.transfer.trip_to.date_end).subtract(time, 'h'));
  } else {
    if (!props.transfer.trip_to.date) {
      onInputTripTo('date', moment());
    }
    onInputTripTo('date_end', moment(props.transfer.trip_to.date).add(time, 'h'));
  }
}

function sendTransferAnalytics(event) {
  emit('send-transfer-analytics', event);
}
</script>

<style lang="scss" scoped>
.hourly-buttons {
  padding-top: 8px;

  @media screen and (max-width: $breakpoint-xs-max) {
    padding: 4px 16px 0 12px;
    margin: 0 -16px;
  }
}
</style>
